/**
 * Primitives of CADINU Verified Deploy. They extend the token and lock pages' night system (Page, Panel, SectionTitle, Gabarito /
 * Figtree / Martian Mono). Colour carries meaning only: verified green for a match, coral for a mismatch or a stop, amber for a
 * warning. Every value from a file, the user or the wallet is rendered as React text.
 */
import styled, { css } from 'styled-components'
import { DISPLAY, MONO, Page, Panel, SectionTitle } from '../TokenPages/ui'

export { DISPLAY, MONO, Page, Panel, SectionTitle }

export const TONE = { ok: '#39d98a', bad: '#ff6b7a', warn: '#f5b453', info: '#9fb4ec' } as const
export type ToneName = keyof typeof TONE

export const Column = styled.div`
  display: grid;
  gap: 16px;
  max-width: 880px;
  margin: 0 auto;
  min-width: 0;
`

export const Hero = styled.header`
  display: grid;
  gap: 10px;
  padding: 12px 0 4px;
`
export const Eyebrow = styled.span`
  font-family: ${MONO};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSubtle};
`
export const H1 = styled.h1`
  margin: 0;
  font-family: ${DISPLAY};
  font-weight: 800;
  font-size: 30px;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text};
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 42px;
  }
`
export const Lead = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSubtle};
  max-width: 640px;
`
export const Promises = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
  li {
    font-size: 13px;
    line-height: 18px;
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.colors.cardBorder};
    color: ${({ theme }) => theme.colors.text};
    background: rgba(159, 182, 255, 0.06);
  }
`

export const StepHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`
export const StepNo = styled.span<{ $state: 'todo' | 'active' | 'done' }>`
  display: inline-grid;
  place-items: center;
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-family: ${MONO};
  font-size: 13px;
  font-weight: 700;
  ${({ $state, theme }) =>
    $state === 'done'
      ? css`
          background: ${TONE.ok}26;
          color: ${TONE.ok};
          border: 1px solid ${TONE.ok}77;
        `
      : $state === 'active'
      ? css`
          background: ${theme.colors.gradientBold};
          color: #f4f6ff;
          border: 0;
        `
      : css`
          background: transparent;
          color: ${theme.colors.textSubtle};
          border: 1px solid ${theme.colors.cardBorder};
        `}
`
export const Muted = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSubtle};
`

export const Drop = styled.label<{ $over: boolean; $error: boolean }>`
  display: grid;
  justify-items: center;
  gap: 6px;
  padding: 28px 16px;
  border-radius: 16px;
  border: 1.5px dashed ${({ $over, $error, theme }) => ($error ? TONE.bad : $over ? theme.colors.primary : theme.colors.cardBorder)};
  background: ${({ $over }) => ($over ? 'rgba(126, 0, 254, 0.08)' : 'rgba(159, 182, 255, 0.03)')};
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
  strong {
    font-family: ${DISPLAY};
    font-size: 18px;
    color: ${({ theme }) => theme.colors.text};
  }
  span {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
`

export const Dl = styled.dl`
  display: grid;
  margin: 0;
  > div {
    display: grid;
    grid-template-columns: minmax(120px, 34%) minmax(0, 1fr);
    gap: 4px 12px;
    padding: 10px 0;
    border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
  }
  > div:first-child {
    border-top: 0;
  }
  dt {
    font-size: 13px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  dd {
    margin: 0;
    min-width: 0;
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.text};
    overflow-wrap: anywhere;
  }
  @media (max-width: 520px) {
    > div {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`
export const Mono = styled.span`
  font-family: ${MONO};
  font-size: 13px;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
`

export const Field = styled.label`
  display: grid;
  gap: 6px;
  min-width: 0;
  > span {
    font-size: 13px;
    line-height: 18px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  > span b {
    color: ${({ theme }) => theme.colors.text};
    font-weight: 700;
  }
`
const control = css`
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: ${({ theme }) => theme.colors.input};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${MONO};
  font-size: 14px;
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
  &[aria-invalid='true'] {
    border-color: ${TONE.bad};
  }
`
export const Input = styled.input`
  ${control}
`
export const TextArea = styled.textarea`
  ${control}
  min-height: 88px;
  resize: vertical;
`
export const Select = styled.select`
  ${control}
`
export const Fields = styled.div`
  display: grid;
  gap: 14px;
`

export const Button = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 52px;
  padding: 0 22px;
  border-radius: 16px;
  cursor: pointer;
  font-family: ${DISPLAY};
  font-weight: 800;
  font-size: 17px;
  ${({ $variant, theme }) =>
    $variant === 'ghost'
      ? css`
          color: ${theme.colors.text};
          background: transparent;
          border: 1px solid ${theme.colors.cardBorder};
        `
      : css`
          color: #f4f6ff;
          border: 0;
          background: ${theme.colors.gradientBold};
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 8px 24px rgba(33, 0, 255, 0.35);
        `}
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    box-shadow: none;
  }
  @media (max-width: 520px) {
    width: 100%;
  }
`
export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`

export const Note = styled.div<{ $tone: ToneName }>`
  padding: 12px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ $tone }) => TONE[$tone]}66;
  background: ${({ $tone }) => TONE[$tone]}14;
  b {
    color: ${({ $tone }) => TONE[$tone]};
  }
`

export const Verdict = styled.div<{ $tone: ToneName }>`
  display: grid;
  gap: 6px;
  padding: 20px;
  border-radius: 20px;
  border: 1px solid ${({ $tone }) => TONE[$tone]}88;
  background: linear-gradient(180deg, ${({ $tone }) => TONE[$tone]}24 0%, ${({ $tone }) => TONE[$tone]}0d 100%);
  strong {
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 24px;
    line-height: 1.15;
    letter-spacing: 0.01em;
    color: ${({ $tone }) => TONE[$tone]};
    ${({ theme }) => theme.mediaQueries.md} {
      font-size: 30px;
    }
  }
  em {
    font-style: normal;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 18px;
    color: ${({ theme }) => theme.colors.text};
  }
`

export const Pill = styled.span<{ $tone: ToneName }>`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  color: ${({ $tone }) => TONE[$tone]};
  border: 1px solid ${({ $tone }) => TONE[$tone]}77;
  background: ${({ $tone }) => TONE[$tone]}1f;
`

export const ExtLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  text-underline-offset: 3px;
`
